import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import { Container } from "@mui/material";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import BusyButton from "../../components/BusyButton";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";

import InputLabel from "@mui/material/InputLabel";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import ProgressBar from "../../components/ProgressBar";

import { doService } from "../../services/ServiceHelper";
import {
  getTeacherActivities,
  repeatActivity,
} from "../../services/ActivityService";
import { getPeriodName } from "../../services/SchoolService";
import { isStringEmpty } from "../../services/StringService";
import { diffInDaysFiltered } from "../../services/DateService";

const RepeatActivity = () => {
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();
  const [showProgress, setShowProgress] = useState(false);
  const [_activities, setActivities] = useState([]);
  const [_selectedActivityEid, setSelectedActivityEid] = useState("XX");
  const [_selectedPeriod, setSelectedPeriod] = useState("XX");

  const [_mondayChecked, setMondayChecked] = useState(false);
  const [_tuesdayChecked, setTuesdayChecked] = useState(false);
  const [_wednesdayChecked, setWednesdayChecked] = useState(false);
  const [_thursdayChecked, setThursdayChecked] = useState(false);
  const [_fridayChecked, setFridayChecked] = useState(false);
  const [_dateStart, setDateStart] = useState(dayjs());
  const [_dateEnd, setDateEnd] = useState(dayjs());
  const [_selectedDays, setSelectedDays] = useState("XX");
  const [_isRunning, setIsRunning] = useState(false);
  const { teacherEid } = useParams();
  
  dayjs.extend(utc);
  dayjs.extend(timezone);

  useEffect(() => {
    initTeacherActivities();
  }, []);

  const initTeacherActivities = () => {
    setShowProgress(true);

    // teacher eid only used if passed from the calling page; otherwise pulls from the current user
    doService(getTeacherActivities(_school, _jwt, teacherEid), (results) => {
      setShowProgress(false);
      setActivities(results.data);
    });
  };

  const handleChangeActivity = (event) => {
    setSelectedActivityEid(event.target.value);
  };

  const handleChangePeriod = (event) => {
    const period = event.target.value;
    setSelectedPeriod(period);
  };

  const validateRepeat = (days) => {
    const title = "Unable to Update Activity Date";
    const messages = [];

    if (_selectedActivityEid === "XX") {
      messages.push("Please select an activity.");
    }
    if (_selectedPeriod === "XX") {
      messages.push("Please select the period.");
    }

    if (isStringEmpty(days)) {
      messages.push("Please select at least one Day.");
    }

    let result = true;
    if (messages.length > 0) {
      result = false;
      window.showErrors(title, messages);
    }

    return result;
  };

  const buildDays = () => {
    let days = "";

    if (_mondayChecked) days += "M";
    if (_tuesdayChecked) days += "T";
    if (_wednesdayChecked) days += "W";
    if (_thursdayChecked) days += "R";
    if (_fridayChecked) days += "F";

    return days;
  };

  const handleSave = () => {
    const days = buildDays();

    if (!validateRepeat(days)) {
      return;
    }

    if (
      !window.confirm(
        `WARNING: You are entering multiple activity dates to your calendar.  Click OK to continue.`
      )
    ) {
      return;
    }

    const dateStartStr = _dateStart.format("YYYY-MM-DD");
    const dateEndStr = _dateEnd.format("YYYY-MM-DD");

    setIsRunning(true);
    doService(
      repeatActivity(
        _school,
        _jwt,
        _selectedActivityEid,
        _selectedPeriod,
        dateStartStr,
        dateEndStr,
        days,
        _selectedDays,
        teacherEid
      ),
      (results) => {
        window.showAlert(
          "Activities Added",
          "Activity Date(s) have been added to the calendar."
        );
      }, null, null, () => {
        setIsRunning(false);
      }
    );
  };

  const handleChangeDays = (event) => setSelectedDays(event.target.value);

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Repeat Activity" showBack={true} />
        <ProgressBar showProgress={showProgress} />

        <Alert severity="info">
          Select the Activity, the Days to Repeat, the Period, and the End Date.
          NOTE: This will overwrite any activities previously planned for those
          periods.
        </Alert>

        <Grid container spacing={1} sx={{ pt: 1 }}>
          <Grid sx={{ pt: "0.5em !important", mb: 0 }} item xs={6} md={6}>
            <InputLabel>Select Activity *</InputLabel>
            <Select
              sx={{ mb: 1 }}
              fullWidth
              size="small"
              onChange={handleChangeActivity}
              value={_selectedActivityEid}
            >
              <MenuItem key="XX" value="XX">
                -- SELECT --
              </MenuItem>
              {_activities.map((activity) => (
                <MenuItem key={activity.eid} value={activity.eid}>
                  {activity.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid sx={{ pt: "0.5em !important", mb: 0 }} item xs={6} md={6}>
            <InputLabel>Select Period *</InputLabel>
            <Select
              sx={{ mb: 1 }}
              fullWidth
              size="small"
              onChange={handleChangePeriod}
              value={_selectedPeriod}
            >
              <MenuItem key="XX" value="XX">
                -- SELECT --
              </MenuItem>
              {[...Array(_school.maxActivityCount)].map((x, i) => (
                <MenuItem key={i + 1} value={i + 1}>
                  {getPeriodName(_school, i)}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid sx={{ pt: "0 !important" }} item xs={12} md={12}>
            <InputLabel>Repeat On *</InputLabel>
            <FormControlLabel
              control={
                <Checkbox
                  checked={_mondayChecked}
                  onChange={(event) => setMondayChecked(event.target.checked)}
                />
              }
              label="M"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={_tuesdayChecked}
                  onChange={(event) => setTuesdayChecked(event.target.checked)}
                />
              }
              label="T"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={_wednesdayChecked}
                  onChange={(event) =>
                    setWednesdayChecked(event.target.checked)
                  }
                />
              }
              label="W"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={_thursdayChecked}
                  onChange={(event) => setThursdayChecked(event.target.checked)}
                />
              }
              label="R"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={_fridayChecked}
                  onChange={(event) => setFridayChecked(event.target.checked)}
                />
              }
              label="F"
            />
          </Grid>
          <Grid sx={{ pt: "0 !important" }} item xs={6} md={6}>
            <InputLabel>Start Date *</InputLabel>
            <MobileDatePicker
              timezone="America/New_York"
              value={_dateStart}
              onChange={(newValue) => {
                setDateStart(newValue);
              }}
            />
          </Grid>
          <Grid sx={{ pt: "0 !important" }} item xs={6} md={6}>
            <InputLabel>Repeats Until *</InputLabel>
            <MobileDatePicker
              timezone="America/New_York"
              value={_dateEnd}
              onChange={(newValue) => {
                setDateEnd(newValue);
              }}
            />
          </Grid>
          <Grid sx={{ pt: "q !important" }} item xs={6} md={6}>
            <InputLabel>Apply to Days in Range</InputLabel>
            <Select
              sx={{ mb: 1 }}
              fullWidth
              size="small"
              onChange={handleChangeDays}
              value={_selectedDays}
            >
              <MenuItem key="XX" value="XX">
                -- ALL DAYS --
              </MenuItem>
              {/* <MenuItem key="A" value="A">
                A Days
              </MenuItem>
              <MenuItem key="B" value="B">
                B Days
              </MenuItem>
              <MenuItem key="C" value="C">
                C Days
              </MenuItem>
              <MenuItem key="D" value="D">
                D Days
              </MenuItem> */}
            </Select>
          </Grid>
        </Grid>
        <BusyButton onClick={handleSave} sx={{ mt: 2 }} variant="contained" busy={_isRunning}>
          Create Dates
        </BusyButton>
      </Container>
    </>
  );
};

export default RepeatActivity;
